/**
 * Plusieurs choix parmi une liste, stockés dans la query string.
 *
 * Paramètre absent = TOUT coché (le défaut ne salit pas l'URL) ; sinon la
 * liste des valeurs cochées, séparées par des virgules, ou `none` si l'on a
 * tout décoché. Survit au rechargement, se partage par lien, comme la période.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useSearchMultiChoice<T extends string>(
  param: string,
  values: readonly T[],
): [T[], (value: T, checked: boolean) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);

  const selected = useMemo<T[]>(() => {
    if (raw === null) return [...values];
    if (raw === 'none') return [];
    // Une valeur inconnue (lien ancien, faute de frappe) est ignorée.
    return raw.split(',').filter((value): value is T => values.includes(value as T));
  }, [raw, values]);

  const toggle = useCallback(
    (value: T, checked: boolean) => {
      const next = checked ? [...new Set([...selected, value])] : selected.filter((item) => item !== value);
      const params = new URLSearchParams(searchParams);
      if (next.length === values.length) params.delete(param);
      else params.set(param, next.length === 0 ? 'none' : values.filter((v) => next.includes(v)).join(','));
      setSearchParams(params, { replace: true });
    },
    [param, values, selected, searchParams, setSearchParams],
  );

  return [selected, toggle];
}
